import { useCallback, useRef } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { createWithOverrides } from '../overrides';

import { useMSRNudgeRecommendations, useMsrSnoozeHandler } from './utils';

type EligibilityCheck = { isEligible: boolean; reason: string };

type Nudge = {
	nudgeEligibility: () => Promise<EligibilityCheck>;
	nudgeKey: string;
	showNudge: ({
		sessionId,
		recommendedNudges,
	}: {
		recommendedNudges: string[];
		sessionId: string;
	}) => void;
};

/*
  Eligibility can be overridden using the following query parameter:

  ?cjn_msr_overrides={
    "feature_visited_too_recently": false,
  }
*/
type OverrideKey = 'feature_visited_too_recently';

export const useExecuteMSRNudge = ({
	cloudId,
	product,
	subProduct,
	onNoMsrResultsReturned,
	onEligibilityReturned,
	onError,
	getFeatureVisited,
	setFeatureVisited,
	getFeatureVisitedCount,
	setFeatureVisitedCount,
	overrides,
}: {
	cloudId: string;
	getFeatureVisited: () => boolean;
	getFeatureVisitedCount: () => number;
	onEligibilityReturned: ({
		eligibilityResult,
		key,
	}: {
		eligibilityResult: EligibilityCheck;
		key: string;
	}) => void;
	onError: (err: unknown) => void;
	onNoMsrResultsReturned: () => void;
	overrides: Partial<Record<OverrideKey, boolean>>;
	product: string;
	setFeatureVisited: (days: number) => void;
	setFeatureVisitedCount: (count: number) => void;
	subProduct: string;
}): (({ nudges }: { nudges: Nudge[] }) => Promise<void>) => {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const sessionId = useRef<string>(uuid());
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({
		product,
		sessionId: sessionId.current,
		cloudId,
		subProduct,
	});
	const { blockEligibilityUntilTomorrow, blockEligibilityToPreventRace, snooze } =
		useMsrSnoozeHandler({
			setFeatureVisited,
			setFeatureVisitedCount,
			getFeatureVisitedCount,
		});
	const withOverrides = createWithOverrides(overrides);

	return useCallback(
		async ({ nudges }: { nudges: Nudge[] }) => {
			try {
				if (withOverrides('feature_visited_too_recently', getFeatureVisited())) {
					return;
				}
				blockEligibilityToPreventRace();

				const eligibilityResults: (Nudge & { isEligible: boolean })[] = await Promise.all(
					nudges.map(async (nudge) => {
						const eligibilityResult = await nudge.nudgeEligibility();
						onEligibilityReturned({ eligibilityResult, key: nudge.nudgeKey });
						return { ...nudge, ...eligibilityResult };
					}),
				);

				const eligibleNudges = eligibilityResults.filter((result) => result.isEligible);

				if (eligibleNudges.length <= 0) {
					return;
				}

				const recommendations = await getMSRNudgeRecommendations();

				const nudgeKeys = nudges.map(({ nudgeKey }) => nudgeKey);
				if (recommendations.filter((rec) => nudgeKeys.includes(rec)).length <= 0) {
					blockEligibilityUntilTomorrow();
					onNoMsrResultsReturned();
					return;
				}

				for (const recommendation of recommendations) {
					const nudge = eligibleNudges.find((nudge) => nudge.nudgeKey === recommendation);
					if (nudge) {
						snooze();
						return nudge.showNudge({
							sessionId: sessionId.current,
							recommendedNudges: recommendations,
						});
					}
				}
			} catch (error: unknown) {
				blockEligibilityUntilTomorrow();
				onError(error);
			}
		},
		[
			getFeatureVisited,
			getMSRNudgeRecommendations,
			blockEligibilityUntilTomorrow,
			blockEligibilityToPreventRace,
			withOverrides,
			snooze,
			onError,
			onNoMsrResultsReturned,
			onEligibilityReturned,
		],
	);
};
